import { Dispatch, SetStateAction, useState } from 'react'

import { Alert, Card, Dialog, Icon, IconButton, Snackbar } from '@mui/material'

import MDBox from 'components/MDBox'
import MDButton from 'components/MDButton'
import MDTypography from 'components/MDTypography'
import LoadingComponent from 'modules/Loading'

interface Props {
	print?: {
		onClicked: () => void
	}
	save?: {
		disabled?: boolean
		onClicked: () => Promise<void>
	}
	deleteItem?: {
		visible: boolean
		onClicked: () => Promise<void>
	}
	formEdited: boolean
	setFormEdited: Dispatch<SetStateAction<boolean>>
}

type NotificationType = 'success' | 'error'
// TODO: Allow custom messages for notifications from API responses. Currently just shows 'Success' or 'Error'
// TODO: Use one state for both notification type and message and visible

function ManageCard({ save, print, deleteItem, formEdited, setFormEdited }: Props) {
	const [notifcationVisible, setNotificationVisible] = useState<boolean>(false)
	const [saveLoading, setSaveLoading] = useState<boolean>(false)
	const [deleteLoading, setDeleteLoading] = useState<boolean>(false)
	const [notifcationType, setNotificationType] = useState<NotificationType>()
	const [deleteDialog, setDeleteDialogOpen] = useState<boolean>(false)

	const handleClose = () => {
		setNotificationVisible(false)
	}

	return (
		<Card>
			<Dialog open={deleteDialog} onClose={() => setDeleteDialogOpen(false)}>
				<MDBox
					width={'30vw'}
					height={'25vh'}
					style={{
						backgroundColor: '#fff',
						overflowY: 'scroll',
						overflowX: 'hidden',
						scrollbarWidth: 'none',
					}}
				>
					<MDBox
						p={3}
						display={'flex'}
						flexDirection={'column'}
						justifyContent={'center'}
						height={'100%'}
						alignItems={'center'}
						gap={1}
					>
						<MDTypography variant="h3">{'Confirm Deletion'}</MDTypography>
						<MDBox
							display={'flex'}
							flexDirection={'column'}
							justifyContent={'space-between'}
							mt={1}
							height={'100%'}
							gap={2}
						>
							<MDTypography variant="body2" color="text" fontWeight="regular">
								Are you sure you want to delete this item? This action cannot be undone.
							</MDTypography>
							<MDButton
								variant="contained"
								color="error"
								onClick={async () => {
									setDeleteDialogOpen(false)
									setDeleteLoading(true)
									try {
										await deleteItem?.onClicked()
										setNotificationType('success')
										setNotificationVisible(true)
									} catch (error) {
										setNotificationType('error')
										setNotificationVisible(true)
										console.error(`Error Deleting: ${error}`)
									}
									setDeleteLoading(false)
								}}
							>
								Confirm Deletion
							</MDButton>
						</MDBox>
					</MDBox>
				</MDBox>
			</Dialog>
			<Snackbar
				open={notifcationVisible}
				autoHideDuration={3000}
				onClose={handleClose}
				anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
				sx={{ justifyContent: 'center', alignItems: 'center' }}
			>
				<Alert
					onClose={handleClose}
					severity={notifcationType === 'success' ? 'info' : 'error'}
					variant="filled"
					sx={{ width: '100%' }}
				>
					{notifcationType === 'success' ? 'Success' : `Error`}
				</Alert>
			</Snackbar>
			<MDBox display={'flex'} flexDirection={'row'} justifyContent={'space-evenly'}>
				{save && saveLoading ? (
					<MDBox display={'flex'} alignItems={'center'} justifyContent={'center'}>
						<LoadingComponent
							type="circular_black"
							height={60}
							width={60}
							speed={0.8}
							containerStyle={{ opacity: 0.4 }}
						/>
					</MDBox>
				) : (
					<IconButton
						size="large"
						disabled={!formEdited || save?.disabled}
						onClick={async () => {
							setSaveLoading(true)
							try {
								await save.onClicked()
								setNotificationType('success')
								setNotificationVisible(true)
								setFormEdited(false)
							} catch (error) {
								setNotificationType('error')
								setNotificationVisible(true)
								setFormEdited(false)
								console.error(`Error Saving: ${error}`)
							}
							setSaveLoading(false)
						}}
						style={{ opacity: formEdited && !save?.disabled ? 1 : 0.3 }}
					>
						<Icon fontSize="large" color="primary">
							save
						</Icon>
					</IconButton>
				)}
				{print && (
					<IconButton size="large" onClick={() => print.onClicked()}>
						<Icon fontSize="large" color="warning">
							print
						</Icon>
					</IconButton>
				)}
				{deleteItem?.visible &&
					(deleteLoading ? (
						<MDBox display={'flex'} alignItems={'center'} justifyContent={'center'}>
							<LoadingComponent
								type="circular_black"
								height={60}
								width={60}
								speed={0.8}
								containerStyle={{ opacity: 0.4 }}
							/>
						</MDBox>
					) : (
						<IconButton
							size="large"
							onClick={async () => {
								setDeleteDialogOpen(true)
							}}
						>
							<Icon fontSize="large" color="error">
								delete_outline
							</Icon>
						</IconButton>
					))}
			</MDBox>
		</Card>
	)
}
export default ManageCard
